import type {Reseller, OrderPriceTable, OrderPriceTableLocation} from '../server/functions/resellers/resellers.types.js'

type ResellerAddress = Reseller['settings']['companyAddress']

// Returns the fixed address for a start/end location of a distance price table, or undefined when the order's own first/last address should be used
function getPriceTableLocationAddress(reseller: Pick<Reseller, 'settings'>, location: OrderPriceTableLocation | undefined): ResellerAddress | undefined {
    if (!location) {
        return undefined
    }

    // 'start' is only valid as end location, it is resolved by getPriceTableEndAddress
    if (location.type === 'start') {
        return undefined
    }

    if (location.type === 'companyAddress') {
        return reseller.settings.companyAddress
    }

    if (location.type === 'branch') {
        return reseller.settings.branches?.find((branch) => branch.id === location.branchId)?.address
    }

    if (location.type === 'custom') {
        return location.address
    }

    return undefined
}

// The end address of a distance price table, an end location of type 'start' returns to the start location (fixed or the order's first address)
function getPriceTableEndAddress<OrderAddress>(reseller: Pick<Reseller, 'settings'>, priceTable: Pick<OrderPriceTable, 'startLocation' | 'endLocation'>, orderAddresses: OrderAddress[]): ResellerAddress | OrderAddress | undefined {
    if (priceTable.endLocation?.type === 'start') {
        return getPriceTableLocationAddress(reseller, priceTable.startLocation) || orderAddresses[0]
    }

    return getPriceTableLocationAddress(reseller, priceTable.endLocation)
}

// A distance price table needs at least two points, with only one order address a fixed start or end location is required
function priceTableHasFixedLocation(priceTable: Pick<OrderPriceTable, 'startLocation' | 'endLocation'>): boolean {
    return [priceTable.startLocation, priceTable.endLocation].some((location) => location && location.type !== 'order' && location.type !== 'start')
}

export {
    getPriceTableLocationAddress,
    getPriceTableEndAddress,
    priceTableHasFixedLocation
}
